<script setup lang="ts">
import { getHostname } from '@@/bits/url'
import type { ImageSearchResult } from '@@/bits/web_search'
import { SearchFilters } from '@@/bits/web_search'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import { fetchJson } from '@padlet/fetch'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

const props = defineProps<{
  results: ImageSearchResult[]
  filter: SearchFilters
}>()

const emit = defineEmits<{
  (name: 'select', url: string, thumbnailUrl: string): void
}>()

const LOADING_IMAGES_BG_COLOR = ['red', 'blue', 'green', 'yellow', 'purple', 'pink', 'cyan', 'orange']

const container = ref<HTMLUListElement>()
const imageWrappers = ref<HTMLLIElement[]>()

const imageWidth = ref(0)

const erroredThumbnailUrls = ref(new Set())

const validResults = computed(() =>
  props.results.filter((result) => !erroredThumbnailUrls.value.has(result.thumbnail_url)),
)
const isShowingAiImages = computed<boolean>(() => props.filter === SearchFilters.AiImages)

const handleResultsUpdate = async (): Promise<void> => {
  await nextTick()
  imageWidth.value = imageWrappers.value && imageWrappers.value[0] ? imageWrappers.value[0].clientWidth : 0
}

const selectWithBeacon = (url: string, thumbnailUrl: string, beaconLink: string): void => {
  if (beaconLink) {
    fetchJson(beaconLink, { cors: true })
  }
  emit('select', url, thumbnailUrl)
}

const providerName = (result: ImageSearchResult): string => {
  if (result.provider_name) return result.provider_name
  return getHostname(result.url)
}

const handleImageError = (erroredUrl) => {
  erroredThumbnailUrls.value = new Set([...erroredThumbnailUrls.value, erroredUrl])
}

watch(() => props.results, handleResultsUpdate)

onMounted((): void => {
  imageWidth.value = (container.value?.clientWidth ?? 0) / 3 - 12
  // After the components are auto-layouted, we assigned the fixed imageWidth so that
  // Beethoven-Vivaldi can load the most size-optimal image
  handleResultsUpdate()
})
</script>

<template>
  <ul
    ref="container"
    :class="[
      'w-full',
      'grid',
      'grid-cols-2',
      !isShowingAiImages || results.length > 4 ? 'md:grid-cols-3' : '',
      'mx-0',
      'gap-4',
      !isShowingAiImages ? 'my-2 pt-1' : 'pt-2 pb-4 md:pb-2 md:px-0.25 md:box-border',
    ]"
  >
    <li
      v-for="(result, index) in validResults"
      ref="imageWrappers"
      :key="result.thumbnail_url"
      :class="['p-1/2', 'flex', 'flex-col', 'relative']"
    >
      <div
        :class="[
          !isShowingAiImages && 'pb-1.5',
          'rounded-2xl',
          'focus:ring-2',
          'focus:ring-grape-500',
          'dark:focus:ring-canary-500',
          'focus:ring-opacity-100',
          'hover-hover:hover:ring-2',
          'hover-hover:hover:ring-grape-500',
          'hover-hover:dark:hover:ring-canary-500',
          'hover-hover:hover:ring-opacity-100',
          'cursor-pointer',
          'flex',
          'flex-col',
        ]"
        :style="isShowingAiImages ? 'width: fit-content' : undefined"
        tabindex="0"
        :data-testid="`contentPickerSearchResultsImage-${index}`"
        @click="selectWithBeacon(result.url, result.thumbnail_url, result.beacon_link)"
        @keydown.enter="selectWithBeacon(result.url, result.thumbnail_url, result.beacon_link)"
        @keydown.space="selectWithBeacon(result.url, result.thumbnail_url, result.beacon_link)"
      >
        <ImageThumbnail
          v-if="result.width && result.height"
          class="rounded-2xl"
          :placeholder-color="LOADING_IMAGES_BG_COLOR[index % 7]"
          :original-image-width="Number(result.width)"
          :original-image-height="Number(result.height)"
          :title="result.title"
          :src="result.thumbnail_url"
          :width="imageWidth"
          :aspect-ratio="result.width / result.height"
          :process-image="isShowingAiImages"
          @error="handleImageError(result.thumbnail_url)"
        />
        <img
          v-else
          class="rounded-2xl"
          :alt="result.title"
          loading="lazy"
          :src="result.thumbnail_url"
          @error="handleImageError(result.thumbnail_url)"
        />
        <span v-if="!isShowingAiImages">
          <div v-if="result.attribution_link" class="mx-2">
            <div class="mt-1.5 mb-0.5">
              <div v-if="providerName(result) === 'Unsplash'" class="text-12-15 text-highway-200 dark:text-white-200">
                {{ __('%{imageAuthorName} on Unsplash', { imageAuthorName: result.title }) }}
              </div>
              <div v-else class="mt-1 text-12-15 text-highway-200 dark:text-white-200 truncate" :title="result.title">
                {{ result.title }}
              </div>
            </div>
            <div class="flex flex-row text-12-15 mt-0.5 ss01-nums text-highway-200 dark:text-white-200 truncate">
              <a
                :class="[
                  'text-12-15',
                  'font-semibold',
                  'text-highway-200',
                  'dark:text-white-200',
                  'shrink-0',
                  'hover-hover:hover:text-grape-500',
                  'hover-hover:dark:hover:text-canary-500',
                  'focus:text-grape-500',
                  'hover-hover:dark:focus:text-canary-500',
                  'rounded',
                  'focus:ring-2',
                  'focus:ring-hover',
                ]"
                :href="result.attribution_link"
                target="_blank"
                :aria-label="__('Go to source of the image')"
                @click.stop=""
              >
                {{ __('Source') }}
              </a>
            </div>
          </div>
          <div v-else class="mx-2">
            <div class="mt-1.5 text-12-15 text-highway-200 dark:text-white-200 truncate">
              {{ providerName(result) }}
            </div>
          </div>
        </span>
      </div>
    </li>
  </ul>
</template>
